import * as React from 'react'
import { useEffect, useState } from 'react'
import { v4 } from 'uuid'
import { FormattedMessage } from 'react-intl'
import { Button, Text } from '@metrisk/react_metrisk-components'

import campaign from '@app/config/campaign'

import PurchaseInformationItem from './PurchaseInformationItem'

import './PurchaseInformation.scss'

const validPurchaseInformationFields = [
  'orderNo',
  'invoiceNo',
  'purchaseDate',
  'placeOfPurchase',
  'purchaseItems',
  'proofOfPurchase',
  'proofOfPackaging',
  'proofOfProduct',
]

const PurchaseInformation = ({
  name,
  value,
  options,
  formFields,
  toRender,
  required,
  onChange,
  upload,
  optionsOverride,
  showAddReceipt = true,
  showRemoveReceipt,
  canDelete,
  ...props
}: any) => {
  const fieldsToRender = toRender ?? options.map((option: any) => option.value)
  const purchaseInformationFields: any = options
    .filter((option: any) => {
      return validPurchaseInformationFields.includes(option.value)
    })
    .map((option: any) => option.value) || []
  const [items, setItems] = useState([])
  // Options are the fields we need to capture
  // There should be the option of adding another item to the array

  useEffect(() => {
    if (optionsOverride) {
      const uniqueOptionsOverrides = Object.values(optionsOverride.reduce((acc: Record<string, any>, option: Record<string, any>) => {
        if (acc[option.id]) {
          return {
            ...acc,
            [option.id]: {
              ...acc[option.id],
              ...option,
              fields: {
                ...acc[option.id].fields,
                ...option.fields,
              },
            },
          }
        }
        return {
          ...acc,
          [option.id]: option,
        }
      }, {}))
      setItems(uniqueOptionsOverrides)
      onChange(uniqueOptionsOverrides)
      return
    }
    const items = [generateNewRow()]
    setItems(items)
    onChange(items)
  }, [])

  const generateNewRow = () => {
    let row: any = {
      id: v4(),
      valid: false,
      fields: {
        quantity: {
          valid: true,
          value: 1,
        }
      },
    }

    purchaseInformationFields.forEach((field: string) => {
      row.fields[field] = {
        valid: false,
        value: null,
      }
    })

    return row
  }

  const handleAddNewRow = () => {
    if (items.length < 4) {
      const updatedItems = [...items, generateNewRow()]
      setItems(updatedItems)
      onChange(updatedItems)
    }
  }

  const handleDeleteRow = (id: string) => {
    if (items.length > 1) {
      const updatedItems = items.filter((item) => item.id !== id)
      setItems(updatedItems)
      onChange(updatedItems)
    }
  }

  const handleItemUpdate = (id: string, field: string, value: any, valid: boolean) => {
    const updatedItems = items.map((item: any) => {
      if (item.id === id) {
        const fields = {
          ...item.fields,
          [field]: {
            valid,
            value,
          },
        }

        return {
          id,
          valid: Object.keys(fields).every((field: any) => fields[field].valid),
          fields,
        }
      }

      return item
    })

    setItems(updatedItems)
    onChange(updatedItems)
  }

  const getFormFields = (item: any) => {
    if (item?.formFields) return item.formFields
    return formFields
  }

  const getCanDelete = () => {
    if (canDelete !== undefined) return canDelete
    if (items.length > 1) return true
    return false
  }

  return (
    <div className="purchase-information">
      <Text><FormattedMessage defaultMessage='Please enter details about your purchase(s) below.'/></Text>

      {items.map((item, index) => (
        <PurchaseInformationItem
          key={item.id}
          index={index}
          item={item}
          upload={upload}
          formFields={getFormFields(item)}
          fieldsToRender={fieldsToRender}
          canDelete={getCanDelete()}
          onItemUpdate={handleItemUpdate}
          onDelete={handleDeleteRow}
        />
      ))}

      {showAddReceipt && items.length < 4 && campaign.maxRedemptions !== 1 && (
        <div className="purchase-information__footer">
          <Button type={'Secondary'} onClick={handleAddNewRow}>
          <FormattedMessage defaultMessage='Add Another Receipt'/>
          </Button>
        </div>
      )}
    </div>
  )
}

export default PurchaseInformation
